import React, { createContext, useState, useEffect, useContext, useRef, useCallback } from 'react';
import api, { getUserProfile, login as apiLogin, logout as apiLogout, googleLink as apiGoogleLink, googleLogin as apiGoogleLogin } from '../../services/api';
import { ensureBrowserSession } from '../../services/browserSession';
import { invalidateInstagramUserSnapshot } from '../../utils/instagramConnectionState';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const isRefreshing = useRef(false);

  const refreshSubscriptionInfo = useCallback(async () => {
    try {
      const response = await api.get('/check-subscription/');
      setSubscriptionInfo(response.data);
      return response.data;
    } catch {
      setSubscriptionInfo(null);
      return null;
    }
  }, []);

  const refreshUser = useCallback(async () => {
    if (isRefreshing.current) return null;
    isRefreshing.current = true;
    try {
      await ensureBrowserSession();
      const response = await getUserProfile();
      setCurrentUser(response.data);
      if (response.data?.user_type === 'business') await refreshSubscriptionInfo();
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        setCurrentUser(null);
        setSubscriptionInfo(null);
      }
      return null;
    } finally {
      isRefreshing.current = false;
      setLoading(false);
    }
  }, [refreshSubscriptionInfo]);

  useEffect(() => {
    refreshUser();
    const interval = setInterval(refreshUser, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [refreshUser]);

  const acceptSession = async (fallbackUser) => {
    // The server has already set an HttpOnly cookie; never accept a token in JS.
    let user = fallbackUser;
    try {
      user = (await getUserProfile()).data;
    } catch (error) {
      if (!user || error.response?.status === 401) throw error;
    }
    if (!user?.user_type) throw new Error('Invalid user profile data');
    setCurrentUser(user);
    const needsProfileCompletion = !user.is_profile_completed;
    localStorage.setItem('needs_profile_completion', String(needsProfileCompletion));
    if (user.user_type === 'business') await refreshSubscriptionInfo();
    return { user, needsProfileCompletion };
  };

  const login = async (username, passwordOrUser) => {
    if (passwordOrUser && typeof passwordOrUser === 'object') return acceptSession(passwordOrUser);
    const response = await apiLogin(username, passwordOrUser);
    if (!response.data?.session_authenticated) throw new Error('Invalid login response from server');
    return acceptSession(response.data.user);
  };

  const loginWithGoogle = async (credential, allowRegistration = false) => {
    const response = await apiGoogleLogin(credential, allowRegistration);
    if (!response.data?.session_authenticated) throw new Error('Invalid login response from server');
    return acceptSession(response.data.user);
  };

  const linkGoogleAccount = async (credential, password) => {
    const response = await apiGoogleLink(credential, password);
    if (!response.data?.session_authenticated) throw new Error('Invalid Google link response from server');
    return acceptSession(response.data.user);
  };

  const logout = async () => {
    // Do not pretend logout succeeded if the server could not revoke the cookie.
    await apiLogout();
    setCurrentUser(null);
    setSubscriptionInfo(null);
    try {
      localStorage.removeItem('foth_is_admin_superuser');
      delete window['ga-disable-G-8EVTEFLFR4'];
    } catch (e) {}
  };

  const invalidateInstagramConnection = useCallback(() => {
    setCurrentUser(invalidateInstagramUserSnapshot);
  }, []);

  return <AuthContext.Provider value={{
    currentUser, loading, login, loginWithGoogle, linkGoogleAccount, logout, refreshUser,
    invalidateInstagramConnection, isAuthenticated: !!currentUser,
    subscriptionInfo, loadingSubscription: false, refreshSubscriptionInfo,
  }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export default AuthContext;
